/* ============
 * Bootstrap File
 * ============
 *
 * Will configure and bootstrap the application
 */


/* ============
 * Vue
 * ============
 *
 * Vue.js is a library for building interactive web interfaces.
 * It provides data-reactive components with a simple and flexible API.
 *
 * http://rc.vuejs.org/guide/
 */
import Vue from 'vue';

Vue.config.debug = process.env.NODE_ENV !== 'production';

const getBridgeBlockedToastMessage = () => {
  const locale = (store.state.profile.account && store.state.profile.account.language) || process.env.language;
  const fallbackLocale = messages[locale] ? locale : 'en';

  return messages[fallbackLocale].static.bridgeHandoff.blockedToast;
};

/* ============
* Key Codes
* =============
*/
Vue.config.keyCodes = {
  decimal: 190,
};


/* ============
 * Vuex Router Sync
 * ============
 *
 * Effortlessly keep vue-Router and vuex store in sync.
 *
 * https://github.com/vuejs/vuex-router-sync/blob/master/README.md
 */
import VuexRouterSync from 'vuex-router-sync';
import store from './app/store';

store.dispatch('checkAuthentication');


/* ============
 * Axios
 * ============
 *
 * Promise based HTTP client for the browser and node.js.
 * Because Vue Resource has been retired, Axios will now been used
 * to perform AJAX-requests.
 *
 * https://github.com/mzabriskie/axios
 */
import Axios from 'axios';
import authService from './app/services/auth';

const logoutErrors = [
  40102, // No token provided
  40103, // Invalid token
];
Axios.defaults.baseURL = process.env.API_LOCATION;
Axios.defaults.headers.common.Accept = 'application/json';
Axios.interceptors.response.use(
  response => response,
  (error) => {
    /**
     * For application in maintenance mode.
     */
    // if (error.message === 'Network Error') {
    //   store.dispatch('setMaintenanceMode', true);
    //   authService.logout();
    //   return Promise.reject(error);
    // }

    /**
     * Phase E/F — when the monolith rejects a stock mutation because the org
     * has been handed off (409) or decommissioned (410), cache the returned
     * state so the banner updates immediately, then let the original promise
     * reject so the calling component can surface its own error.
     */
    const data = error.response && error.response.data;
    const isBridgeHandoffRejection = data &&
      (data.state === 'handed_off' || data.state === 'decommissioned') &&
      (error.response.status === 409 || error.response.status === 410);
    if (isBridgeHandoffRejection) {
      store.dispatch('setBridgeHandoff', {
        stock_mutations_handed_off: true,
        stock_decommissioned: data.state === 'decommissioned',
        inventory_app_url: data.inventory_app_url || '',
      });

      if (Vue.$toast) {
        Vue.$toast.open({
          message: getBridgeBlockedToastMessage(),
          type: 'warning',
          duration: 6000,
          position: 'top-right',
          queue: false,
        });
      }
    }

    /**
     * If response is unauthorized and it is not a request retry from auth service.
     */
    if (error.response.status === 401 && error.response.request.responseURL.indexOf('retry=1') === -1) {
      const errorCode = error.response.data.error.code;
      /**
       * If error should log user out.
       */
      if (logoutErrors.indexOf(errorCode) >= 0) {
        authService.logout();
        return Promise.reject(error);
      }

      /**
       * If token is expired, try to refresh it and retry failed ajax.
       */
      if (errorCode === 40104) {
        return authService.token.getRefreshedToken(error.response);
      }

      /**
       * if email is not verified.
       */
      if (
        errorCode === 40113 &&
        Vue.router.history.current.matched.some(record => record.meta.emailVerificationRequired)
      ) {
        Vue.router.push({
          name: 'dashboard',
        });
      }

      /**
       * If user does not have module.
       */
      if (errorCode === 40112) {
        Vue.router.push({
          name: 'dashboard',
        });
      }
    }
    return Promise.reject(error);
  });
Axios.interceptors.request.use((config) => {
  if (localStorage.getItem('id_token')) {
    config.headers.Authorization = `Bearer ${localStorage.getItem('id_token')}`;
  }
  return config;
}, error => Promise.reject(error));
Vue.$http = Axios;

/* ============
 * Vue Router
 * ============
 *
 * The official Router for Vue.js. It deeply integrates with Vue.js core
 * to make building Single Page Applications with Vue.js a breeze.
 *
 * http://router.vuejs.org/en/index.html
 */
import VueRouter from 'vue-router';
import routes from './app/routes';
import routeMidlleware from './app/route-middleware';

Vue.use(VueRouter);

export const router = new VueRouter({
  routes,
});
router.beforeEach(routeMidlleware.beforeEach);

VuexRouterSync.sync(store, router);

Vue.router = router;


/* ============
 * Vue i18n
 * ============
 *
 * Internationalization plugin of Vue.js
 *
 * https://kazupon.github.io/vue-i18n/
 */
import VueI18n from 'vue-i18n';

Vue.use(VueI18n);

/* ============
 * Element User Interface
 * ============
 *
 * Vue.js UI components.
 *
 * http://element.eleme.io/#/en-US
 */
import ElementUI from 'element-ui';

import messages from './app/locale';
import numberFormats from './app/numberFormats';
import moment from 'moment';

moment.locale(process.env.language === 'no' ? 'nb' : process.env.language);
const i18n = new VueI18n({
  locale: process.env.language, // set locale
  messages, // set locale messages
  numberFormats, // set number formats
  fallbackLocale: 'en',
});

Vue.use(ElementUI, {
  i18n: key => i18n.t(key),
});

export default {
  router,
};

/* ============
 * Vue2 google maps
 * ============
 *
 * Vue.js google map integration.
 *
 * https://www.npmjs.com/package/vue2-google-maps
 */
import * as VueGoogleMaps from 'vue2-google-maps';

Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.GOOGLE_MAPS_KEY,
    libraries: 'places', // This is required if you use the Autocomplete plugin
  },
});

Vue.filter('numberformat', (value) => {
  if (isNaN(value)) {
    return value;
  }
  value = parseFloat(value).toFixed(2);

  return value.replace(/\d(?=(\d{3})+\.)/g, '$&,');
});

/**
 * Toast message
 */
import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';
import 'vue-toast-notification/dist/theme-sugar.css';

Vue.use(VueToast);



// WEBPACK FOOTER //
// ./src/bootstrap.js